import isNil from 'lodash/isNil';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { IPairWallet } from 'api/apiPairs/models';
import { useScrollPagination } from 'hooks/utility';
import { TOTAL_TOKEN_ID } from 'shared/consts/numbers';
import { PAIR_WALLETS_RELOAD, STOP_RELOAD_PREFIX } from 'shared/consts/reload';
import { useTypedDispatch } from 'store';
import { useFilters } from 'store/slices/filters/hooks/useFilters';
import { dropAlertState, setAlertState, setCopyPopupShow } from 'store/slices/ui';
import { bnFrom, Bus } from 'tools';
import { EDexPairFilters } from 'types/filters';
import { IDexPair } from 'types/pairs';
import { IWalletTableRow } from 'types/tables/dex/walletTableRow';
import { copyToClipboard } from 'utils/clipboard';
import { formatFiat } from 'utils/formats/formatFiat';
import { formatToken } from 'utils/formats/formatToken';
import { EExchange } from 'web3';

import { useDexWallets } from '../api';

interface IUseDexWalletsRecordsProps {
  dexPair: IDexPair;
  initialSelected?: Record<number, boolean>;
  onChangeSelected?: (n: number[]) => void;
}

export const useDexWalletsRecords = ({
  dexPair,
  initialSelected,
  onChangeSelected,
}: IUseDexWalletsRecordsProps) => {
  const dispatch = useTypedDispatch();
  const { settedPairFilters } = useFilters({
    dex: {
      pair: dexPair,
      type: EDexPairFilters.wallets,
    },
  });
  const getWallets = useDexWallets({ dexPair });

  const getRecords = useCallback(
    async ({
      limit,
      lastSeenId,
    }: {
      limit: number;
      lastSeenId?: number;
    }): Promise<{ records: IPairWallet[]; has_more: boolean } | undefined> => {
      try {
        const { isSuccess, errorMessage, data } = await getWallets({
          lastSeenId,
          limit,
          filters: settedPairFilters,
        });

        if (isSuccess && data) {
          setConnectedWallets(data.total_connected_wallets);

          return {
            records: data.items,
            has_more: data.has_next,
          };
        } else {
          dispatch(
            setAlertState({
              type: 'failed',
              text: errorMessage ?? 'Something went wrong',
              onClose: () => dispatch(dropAlertState()),
              onSubmit: () => dispatch(dropAlertState()),
            }),
          );
          return undefined;
        }
      } catch (error) {
        console.log(error);
        return undefined;
      }
    },
    [dispatch, getWallets, settedPairFilters],
  );

  const { hasMore, records, setRecords, loading, loadMore, remount } = useScrollPagination({
    limit: 200,
    getRecords,
    remountLoad: true,
  });

  const initialSelectedRows = useMemo(() => {
    if (!initialSelected) return {};

    return Object.keys(initialSelected).reduce(
      (acc, val) => {
        const recordIdx = records?.findIndex(el => el.id === Number(val));

        if (recordIdx !== undefined && recordIdx >= 0) {
          acc[recordIdx] = true;
        }

        return acc;
      },
      {} as Record<number, boolean>,
    );
  }, [records, initialSelected]);

  const [connectedWallets, setConnectedWallets] = useState<number>(0);
  const [virtualRecords, setVirtualRecords] = useState<IWalletTableRow[]>([]);
  const [rowSelection, setRowSelection] = useState<Record<number, boolean>>(initialSelectedRows);

  useUpdateEffect(() => {
    // Save the currently selected IDs
    const currentSelectedIds = Object.keys(rowSelection)
      .filter(key => rowSelection[Number(key)])
      .map(key => records?.[Number(key)]?.id)
      .filter(Boolean);

    // Restore selection with updated indices after records change
    const newSelection: Record<number, boolean> = {};
    currentSelectedIds.forEach(id => {
      const newIndex = records?.findIndex(r => r.id === id);
      if (newIndex !== undefined && newIndex >= 0) {
        newSelection[newIndex] = true;
      }
    });

    setRowSelection(newSelection);
  }, [records]);

  const copyAllRecords = useCallback(async () => {
    try {
      let hasMore = true;
      let lastSeenId: number | undefined = undefined;
      const records: IPairWallet[] = [];

      while (hasMore) {
        const data = await getRecords({ limit: 1000, lastSeenId });

        records.push(...(data?.records ?? []));

        hasMore = data?.has_more ?? false;
        lastSeenId = data ? (data.records[data.records.length - 1]?.id ?? undefined) : undefined;
      }

      if (records.length !== 0) {
        const innerText = records.reduce(
          (acc, row, idx) => acc + row.address + (idx !== records.length - 1 ? '\n' : ''),
          '',
        );

        await copyToClipboard(innerText);
        dispatch(setCopyPopupShow(true));
      }
    } catch (error) {
      console.log('error: ', error);
    }
  }, [getRecords, dispatch]);

  useUpdateEffect(() => {
    onChangeSelected?.(
      Object.keys(rowSelection)
        .filter(key => !!rowSelection[key as unknown as number])
        .map(key => records?.[key as unknown as number]?.id)
        .filter(Boolean) as number[],
    );
  }, [records, rowSelection]);

  const handleRemount = useCallback(() => {
    setRecords(undefined);
    setRowSelection({});
    remount();
  }, [remount, setRecords]);

  const selectedRecords = useMemo(() => {
    const selectedRows: IPairWallet[] = [];

    if (!records) return [];

    for (const key of Object.keys(rowSelection)) {
      selectedRows.push(records[Number(key)]);
    }

    return selectedRows;
  }, [rowSelection, records]);

  const updateVisibleRecords = useCallback(async () => {
    const lastSeenId =
      (records ?? []).length < 20
        ? undefined
        : virtualRecords[0]
          ? virtualRecords[0].id
          : undefined;
    const limit = virtualRecords.length;

    if (limit === 0 || !records) {
      Bus.emit(STOP_RELOAD_PREFIX + PAIR_WALLETS_RELOAD);
      return;
    }

    try {
      const startTime = Date.now();

      const { isSuccess, errorMessage, data } = await getWallets({
        lastSeenId: lastSeenId ? lastSeenId - 1 : undefined,
        limit,
        filters: settedPairFilters,
      });

      const endTime = Date.now();

      if (isSuccess && data) {
        const firstSeenIndex = isNil(lastSeenId)
          ? 0
          : records.findIndex(el => el.id === lastSeenId);
        const newRecords = [...records];

        newRecords.splice(firstSeenIndex, data.items.length, ...data.items);

        setRecords(newRecords);

        if (endTime - startTime >= 1500) {
          Bus.emit(STOP_RELOAD_PREFIX + PAIR_WALLETS_RELOAD);
        } else {
          setTimeout(
            () => {
              Bus.emit(STOP_RELOAD_PREFIX + PAIR_WALLETS_RELOAD);
            },
            1500 - (endTime - startTime),
          );
        }
      } else {
        dispatch(
          setAlertState({
            type: 'failed',
            text: errorMessage ?? 'Something went wrong',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => dispatch(dropAlertState()),
          }),
        );
        Bus.emit(STOP_RELOAD_PREFIX + PAIR_WALLETS_RELOAD);
      }
    } catch (error) {
      console.log(error);
      Bus.emit(STOP_RELOAD_PREFIX + PAIR_WALLETS_RELOAD);
    }
  }, [virtualRecords, records, dispatch, getWallets, settedPairFilters, setRecords]);

  useEffect(() => {
    const onFilter = () => {
      handleRemount();
    };

    Bus.on(`${EExchange.dex}:${EDexPairFilters.wallets}:FILTER`, onFilter);

    return () => {
      Bus.off(`${EExchange.dex}:${EDexPairFilters.wallets}:FILTER`, onFilter);
    };
  }, [handleRemount]);

  useEffect(() => {
    Bus.on(PAIR_WALLETS_RELOAD, updateVisibleRecords);

    return () => {
      Bus.off(PAIR_WALLETS_RELOAD, updateVisibleRecords);
    };
  }, [updateVisibleRecords]);

  const tableRecords = useMemo(() => {
    return records
      ? records.map(record => {
          const walletAddressCol = { address: record.address };

          const tokenBaseId = dexPair.token_base.id;
          const rowTokenBaseInfo = record.tokens?.find(token => token.token.id === tokenBaseId);
          const tokenBaseCol = {
            value: rowTokenBaseInfo
              ? formatToken(bnFrom(rowTokenBaseInfo.balance), rowTokenBaseInfo.token.decimals)
              : '',
          };

          const tokenQuoteId = dexPair.token_quote.id;
          const rowTokenQuoteInfo = record.tokens?.find(token => token.token.id === tokenQuoteId);
          const tokenQuoteCol = {
            value: rowTokenQuoteInfo
              ? formatToken(bnFrom(rowTokenQuoteInfo.balance), rowTokenQuoteInfo.token.decimals)
              : '',
          };

          const tokenFeeId = dexPair.token_fee.id;
          const rowTokenFeeInfo = record.tokens?.find(token => token.token.id === tokenFeeId);
          const tokenFeeCol = {
            value: rowTokenFeeInfo
              ? formatToken(bnFrom(rowTokenFeeInfo.balance), rowTokenFeeInfo.token.decimals)
              : '',
          };

          const totalInfo = record.tokens?.find(token => token.token.id === TOTAL_TOKEN_ID);
          const totalUsdCol = {
            value: totalInfo ? formatFiat(bnFrom(totalInfo.balance_usd), 6) : '',
          };
          const transactionCount = totalInfo?.transactions_count;
          const txsCol = { transactionCount: transactionCount as number };

          const connectedBotsCol = { row: record };

          return {
            walletAddressCol,
            tokenBaseCol,
            tokenQuoteCol,
            tokenFeeCol,
            totalUsdCol,
            txsCol,
            connectedBotsCol,
            id: record.id,
            index: record.index,
          } as IWalletTableRow;
        })
      : [];
  }, [records, dexPair]);

  const result = useMemo(
    () => ({
      records: { get: records, set: setRecords },
      copyAllRecords,
      connectedWallets,
      virtualRecords: { get: virtualRecords, set: setVirtualRecords },
      tableRecords,
      hasMore,
      loading,
      loadMore,
      handleRemount,
      rowSelection: { get: rowSelection, set: setRowSelection },
      selectedRecords,
    }),
    [
      records,
      copyAllRecords,
      setRecords,
      connectedWallets,
      hasMore,
      loading,
      loadMore,
      handleRemount,
      selectedRecords,
      rowSelection,
      setRowSelection,
      virtualRecords,
      setVirtualRecords,
      tableRecords,
    ],
  );

  return result;
};
